import { ETipoImpresion } from '@core/enums/impresora.enum';
import { ETipoDocumentoSUNAT } from '@core/enums/tipoDocumentoSUNAT.enum';
import { IEmpresaData } from '@core/interfaces/entidades/empresa.entidad';
import {
  ImpresoraDatosComandaRq,
  ImpresoraDatosVentaRq,
} from '@core/interfaces/request-response/impresora.dto.interface';
import { TemplateEngine } from './template-engine';
import {
  formatFecha,
  formatIgvLabel,
  normalizeIgvPorcentaje,
  numeroALetras,
  toNumber,
  truncateText,
} from './convertidor.utils';

// ==================== MAPA DOCUMENTOS ====================
export const tipoDocumentoMap: Record<ETipoDocumentoSUNAT, string> = {
  [ETipoDocumentoSUNAT.FACTURA]: 'FACTURA ELECTRÓNICA',
  [ETipoDocumentoSUNAT.BOLETA]: 'BOLETA DE VENTA',
  [ETipoDocumentoSUNAT.NOTA_VENTA]: 'NOTA DE VENTA',
  [ETipoDocumentoSUNAT.NOTA_CREDITO]: 'NOTA DE CRÉDITO',
};

export const buildImpresion = async (
  empresa: IEmpresaData,
  tipoImpresion: ETipoImpresion,
  data: Record<string, any>,
  footerMensaje?: string,
): Promise<string> => {
  switch (tipoImpresion) {
    case ETipoImpresion.VENTA:
      return buildVentaHtml(empresa, data, footerMensaje);

    case ETipoImpresion.COMANDA:
      return buildComandaHtml(data);

    case ETipoImpresion.PREVENTA:
      return buildPreventaHtml(data);

    default:
      return '<p>Impresión genérica</p>';
  }
};

export const FOOTER_MENSAJE_DEFAULT = '¡Gracias por su compra!';

export const obtenerFooterMensajePorDocumento = (
  sucursal:
    | {
        footer_factura?: string;
        footer_boleta?: string;
        footer_nota_venta?: string;
      }
    | null
    | undefined,
  tipoDocumento: ETipoDocumentoSUNAT | undefined,
): string | undefined => {
  if (!sucursal || !tipoDocumento) return undefined;
  switch (tipoDocumento) {
    case ETipoDocumentoSUNAT.FACTURA:
      return sucursal.footer_factura || FOOTER_MENSAJE_DEFAULT;
    case ETipoDocumentoSUNAT.BOLETA:
      return sucursal.footer_boleta || FOOTER_MENSAJE_DEFAULT;
    case ETipoDocumentoSUNAT.NOTA_VENTA:
      return sucursal.footer_nota_venta || FOOTER_MENSAJE_DEFAULT;
    default:
      return FOOTER_MENSAJE_DEFAULT;
  }
};

// ==================== VENTA (TEMPLATE) ====================
const buildVentaHtml = async (
  empresa: IEmpresaData,
  data: Record<string, any>,
  footerMensaje?: string,
): Promise<string> => {
  const { documento_sunat, cliente, productos_comprados, descuento, fecha_venta, igv_porcentaje } =
    data as ImpresoraDatosVentaRq;

  const descuentoGlobal = toNumber(descuento || 0);
  const productos: any[] = [];

  let subtotal = 0;

  (productos_comprados as any[]).forEach((prod) => {
    const precioUnitario = toNumber(prod.precio_unitario || 0);
    const precioTotal = precioUnitario * prod.cantidad;
    subtotal += precioTotal;

    productos.push({
      PRODUCTO_CANTIDAD: prod.cantidad,
      PRODUCTO_NOMBRE: truncateText(prod.nombre || 'Producto', 25),
      PRODUCTO_TOTAL: precioTotal.toFixed(2),
    });
  });

  const total = subtotal - descuentoGlobal;

  // Separar IGV del total final
  const igvPorcentaje = normalizeIgvPorcentaje(igv_porcentaje);
  const totalGravado = total / (1 + igvPorcentaje);
  const igv = total - totalGravado;

  // Convertir total a texto
  const totalEntero = Math.floor(total);
  const centimos = Math.round((total - totalEntero) * 100);
  const totalTexto = `${numeroALetras(totalEntero)} CON ${centimos
    .toString()
    .padStart(2, '0')}/100 SOLES`;

  const templateData = {
    EMPRESA_NOMBRE: empresa.nombre,
    EMPRESA_RUC: empresa.ruc,
    EMPRESA_DIRECCION: empresa.direccion || '',
    DOCUMENTO_TIPO: tipoDocumentoMap[documento_sunat.tipo_documento_sunat] || 'COMPROBANTE',
    DOCUMENTO_SERIE: documento_sunat.serie_documento,
    DOCUMENTO_NUMERO: documento_sunat.numero_documento.toString().padStart(8, '0'),
    FECHA_VENTA: formatFecha(fecha_venta),
    CLIENTE_NOMBRE: cliente.nombre_completo,
    CLIENTE_RUC_DNI: cliente.dniRuc,
    PRODUCTOS: productos,

    SUBTOTAL: subtotal.toFixed(2),
    DESCUENTO: descuentoGlobal.toFixed(2),
    MOSTRAR_DESCUENTO: descuentoGlobal > 0,
    TOTAL_GRAVADO: totalGravado.toFixed(2),
    IGV: igv.toFixed(2),
    IGV_PORCENTAJE: formatIgvLabel(igvPorcentaje),
    TOTAL: total.toFixed(2),
    TOTAL_TEXTO: totalTexto,
    FOOTER_MENSAJE: (footerMensaje || FOOTER_MENSAJE_DEFAULT).replace(/\n/g, '<br>'),
  };

  return await TemplateEngine.loadAndRender('print-templates/venta-v0.6.html', templateData);
};

// ==================== COMANDA (TEMPLATE) ====================
const buildComandaHtml = async (data: Record<string, any>): Promise<string> => {
  const { nombre_mesa, productos, fecha_venta, colaborador_nombre } =
    data as ImpresoraDatosComandaRq;

  const productosData: any[] = [];

  (productos as any[]).forEach((prod) => {
    productosData.push({
      PRODUCTO_CANTIDAD: prod.cantidad,
      PRODUCTO_NOMBRE: truncateText(prod.nombre_producto, 32),
      PRODUCTO_NOTA: prod.nota || '',
    });
  });

  const templateData = {
    MESA_NOMBRE: nombre_mesa,
    FECHA_VENTA: formatFecha(fecha_venta),
    COLABORADOR_NOMBRE: colaborador_nombre || 'Sin colaborador',
    PRODUCTOS: productosData,
  };

  return TemplateEngine.loadAndRender('print-templates/comanda-v0.6.html', templateData);
};

const buildPreventaHtml = async (data: Record<string, any>): Promise<string> => {
  const { nombre_mesa, productos, fecha_venta } = data as ImpresoraDatosComandaRq;
  console.log('Data para preventa:', productos);
  const productosData: any[] = [];
  (productos as any[]).forEach((prod) => {
    productosData.push({
      PRODUCTO_CANTIDAD: prod.cantidad,
      PRODUCTO_NOMBRE: truncateText(prod.nombre_producto, 32),
      PRODUCTO_PRECIO: Number(prod.precio_unitario).toFixed(2),
      PRODUCTO_TOTAL: (Number(prod.precio_unitario) * prod.cantidad).toFixed(2),
    });
  });

  const total = productos.reduce(
    (acc, prod) => acc + Number(prod.precio_unitario) * prod.cantidad,
    0,
  );

  const templateData = {
    MESA_NOMBRE: nombre_mesa,
    FECHA_VENTA: formatFecha(fecha_venta),
    PRODUCTOS: productosData,
    TOTAL: total.toFixed(2),
  };
  return TemplateEngine.loadAndRender('print-templates/preventa-v0.6.html', templateData);
};
